import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { useDeleteWorkOrderExceptionMutation } from 'api/work-order/workOrderException'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import T from 'T'
import { handleError } from 'utils/error'
import { getExceptionDelayMs } from 'utils/exceptionFields'
import { get } from 'utils/lodash'

import ExceptionDetailModal from '../exception-detail-modal/ExceptionDetailModal'

import ExceptionAuditCaption from './ExceptionAuditCaption'
import ExceptionCardBody from './ExceptionCardBody'
import ExceptionCardHeader from './ExceptionCardHeader'
import ExceptionCardImage from './ExceptionCardImage'

import type { WorkOrderContainer } from '../../containers/types'
import type { ExceptionCardClickHandler, ExceptionImageItem, ExceptionShape } from './types'

const HHConfirmDialogUntyped = HHConfirmDialog as React.ElementType

type ExceptionCardProps = {
  accountId?: string
  workOrderId?: string
  exception: ExceptionShape
  exceptionType?: string
  date?: string
  time?: string
  images?: ExceptionImageItem[]
  note?: string | null
  description?: string | null
  serviceAction?: string | null
  containers?: WorkOrderContainer[]
  revisions?: unknown[]
  onClick?: ExceptionCardClickHandler
  PageProps?: Record<string, unknown>
  ImageSx?: Record<string, unknown>
}

const ExceptionCard: React.FC<ExceptionCardProps> = ({
  accountId,
  workOrderId,
  exception,
  exceptionType,
  date,
  time,
  images = [],
  note,
  description,
  serviceAction,
  containers = [],
  revisions,
  onClick,
  PageProps,
  ImageSx,
}) => {
  const id = get(exception, 'id')
  const delayTime = getExceptionDelayMs(exception)
  const hasImages = Array.isArray(images) && images.length > 0

  const [isImageView, setIsImageView] = useState(true)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [deleteException, { isLoading: isDeleteLoading }] = useDeleteWorkOrderExceptionMutation()

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
    setMenuAnchor(null)
  }, [])

  const handleOpenDeleteModal = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    setMenuAnchor(null)
    setIsDeleteModalOpen(true)
  }, [])

  const handleCloseDeleteModal = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
    setMenuAnchor(null)
    setIsDeleteModalOpen(false)
  }, [])

  const handleEditModalOpen = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    setMenuAnchor(null)
    setIsEditModalOpen(true)
  }, [])

  const handleEditModalClose = useCallback((event?: React.SyntheticEvent) => {
    event?.stopPropagation()
    setIsEditModalOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      deleteException({ id })
        .unwrap()
        .then(() => {
          toast.success(T.EXCEPTION_DELETED_SUCCESS)
          setIsDeleteModalOpen(false)
        })
        .catch(handleError)
    },
    [deleteException, id]
  )

  const handleToggleView = useCallback(() => setIsImageView(prev => !prev), [])

  // Card click opens the detail drawer; the fullscreen gallery opens from the media
  // inside the drawer (better discoverability of the editable fields + audit).
  const handleCardClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if ((event.target as HTMLElement).closest('.overlay')) return
    setIsEditModalOpen(true)
  }, [])

  const handleModalMediaClick = useCallback(() => onClick?.(exception), [exception, onClick])

  return (
    <>
      {/* minHeight (not height): the body gained optional rows (meta chips, Description) that
          must grow the card instead of clipping inside the swiper slide. Flex column so the
          audit caption pins to the card bottom regardless of body height. */}
      <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: 482, width: 275 }} onClick={handleCardClick}>
        <ExceptionCardHeader exceptionType={exceptionType} onMenuOpen={handleMenuOpen} />

        <ExceptionCardImage
          accountId={accountId}
          date={date}
          exception={exception}
          images={images}
          ImageSx={ImageSx}
          isImageView={isImageView}
          PageProps={PageProps}
          time={time}
          workOrderId={workOrderId}
          onToggleView={handleToggleView}
        />

        <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mt: isImageView ? 0 : -2, pt: 0.5 }}>
          <ExceptionCardBody
            containers={containers}
            delayTime={delayTime}
            description={description}
            exception={exception}
            exceptionType={exceptionType}
            note={note}
            serviceAction={serviceAction}
          />
          <Box sx={{ mt: 'auto' }}>
            <ExceptionAuditCaption exception={exception} revisions={revisions} />
          </Box>
        </CardContent>

        <Menu
          anchorEl={menuAnchor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={Boolean(menuAnchor)}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditModalOpen}>{T.EXCEPTION_DETAIL}</MenuItem>
          <MenuItem onClick={handleOpenDeleteModal}>
            <Typography color="error" fontWeight="normal" variant="h6">
              {T.DELETE_EXCEPTION}
            </Typography>
          </MenuItem>
        </Menu>
      </Card>

      <HHConfirmDialogUntyped
        confirmButtonTitle={T.DELETE}
        confirmDescription={<Typography component="p">{T.DELETE_EXCEPTION_CONFIRM_MSG}</Typography>}
        confirmTitle={T.CONFIRM_EXCEPTION_DELETION}
        isOpen={isDeleteModalOpen}
        loading={isDeleteLoading}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      <ExceptionDetailModal
        accountId={accountId}
        date={date}
        exception={exception}
        images={images}
        note={note}
        open={isEditModalOpen}
        revisions={revisions}
        time={time}
        workOrderId={workOrderId}
        onClose={handleEditModalClose}
        onMediaClick={hasImages ? handleModalMediaClick : undefined}
      />
    </>
  )
}

export default ExceptionCard
